"use client";

import { useEffect, useRef, useCallback, useState } from "react";
import { useLatest } from "./use-latest";
import { useDispatch } from "react-redux";
import { putDraft, getDraft, deleteDraft } from "../lib/offlineDb";
import { setDraftSavedAt } from "../redux/slices/offlineSlice";

/**
 * Phase 2 — autosave in-progress form data to IndexedDB while the auditor
 * types, independent of the server-save cadence (`useDebouncedSave`).
 *
 * This protects work that hasn't been submitted to the server yet: if the
 * app closes, the tab reloads, or the phone loses power, the draft can be
 * restored from IndexedDB the next time the form mounts.
 *
 * Usage:
 *   const { restoreDraft, clearSavedDraft, lastDraftSavedAt } =
 *     useDraftAutosave(`report:${reportId}:site-overview`, formData);
 *
 *   // on mount, before the server data effect overwrites formData:
 *   useEffect(() => {
 *     restoreDraft().then((draft) => {
 *       if (draft) setFormData(draft.data);
 *     });
 *   }, []);
 */
export function useDraftAutosave(
  draftKey: string | null,
  data: unknown,
  options: { debounceMs?: number } = {},
) {
  const { debounceMs = 1500 } = options;
  const dataRef = useLatest(data);
  const serializedData = JSON.stringify(data);
  const dispatch = useDispatch();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [lastDraftSavedAt, setLastDraftSavedAt] = useState<number | null>(null);
  const skipNextSaveRef = useRef(false);

  useEffect(() => {
    if (!draftKey) return;
    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      return;
    }

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    const draftData = dataRef.current;
    timeoutRef.current = setTimeout(() => {
      putDraft({ id: draftKey, data: draftData })
        .then((record) => {
          setLastDraftSavedAt(record.updatedAt);
          dispatch(
            setDraftSavedAt({ key: draftKey, timestamp: record.updatedAt }),
          );
        })
        .catch((error) => {
          console.log("[v0] Failed to autosave draft:", error?.message);
        });
    }, debounceMs);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [draftKey, serializedData, debounceMs, dispatch, dataRef]);

  const restoreDraft = useCallback(async () => {
    if (!draftKey) return null;
    try {
      const draft = await getDraft(draftKey);
      return draft || null;
    } catch (error) {
      console.log("[v0] Failed to restore draft:", error?.message);
      return null;
    }
  }, [draftKey]);

  const clearSavedDraft = useCallback(async () => {
    if (!draftKey) return;
    skipNextSaveRef.current = true;
    try {
      await deleteDraft(draftKey);
      setLastDraftSavedAt(null);
    } catch (error) {
      console.log("[v0] Failed to clear draft:", error?.message);
    }
  }, [draftKey]);

  return { restoreDraft, clearSavedDraft, lastDraftSavedAt };
}
